import React from 'react';
import { Box, Flex } from '@qga/roo-ui/components';
import { compose } from 'ramda';

import StarRating from '@shared/v2/components/StarRating';

import Heading from '@/components/Heading';
import Text from '@/components/Text';
import PropertyDestination from './components/PropertyDestination';
import PropertyTags from './components/PropertyTags';

const dataTestId = 'PROPERTY_TITLE';

interface PropertyTitleProps extends React.HTMLAttributes<HTMLDivElement> {
  destinationName?: string;
  propertyName: string;
  rating: number;
  ratingType: 'AAA' | 'SELF_RATED';
  size?: 'PAGE' | 'CARD';
  tags?: Array<{ name: string }>;
  tagline?: string;
}

const PropertyTitle = ({
  destinationName,
  propertyName,
  rating,
  ratingType,
  size = 'CARD',
  tags = [],
  tagline,
  ...props
}: PropertyTitleProps) => (
  <Flex flexDirection="column" gap={'4px'} data-testid={dataTestId} {...props}>
    {destinationName && (
      <PropertyDestination
        destinationName={destinationName}
        iconSize={20}
        fontSize={'xs'}
      />
    )}

    {size === 'PAGE' ? (
      <Heading.h1
        data-testid={`${dataTestId}_PROPERTY_NAME`}
        fontSize={['24px', null, '2xl']}
        lineHeight={['30px', null, '40px']}
      >
        {propertyName}
      </Heading.h1>
    ) : (
      <Heading.h4
        data-testid={`${dataTestId}_PROPERTY_NAME`}
        fontSize={['18px', null, '20px']}
        lineHeight={['22px', null, '25px']}
      >
        {propertyName}
      </Heading.h4>
    )}
    <Flex
      alignItems={[null, null, 'center']}
      flexDirection={['column', null, 'row']}
      gap={['4px', null, '8px']}
    >
      <StarRating
        data-testid={`${dataTestId}_STAR_RATING`}
        rating={rating}
        ratingType={ratingType}
        size={ratingType === 'AAA' ? 20 : 14}
      />
      {tags && <PropertyTags data-testid={`${dataTestId}_TAGS`} tags={tags} />}
    </Flex>

    {tagline && (
      <Box mt="2">
        <Text fontSize={['xs', null, 'sm']} lineHeight="24px">
          {tagline}
        </Text>
      </Box>
    )}
  </Flex>
);

export default compose(React.memo)(PropertyTitle);
